import { UploadSimple, X } from "@phosphor-icons/react"
import { Button } from "@tokenterminal/core/ui/button/Button"
import { IconButton } from "@tokenterminal/core/ui/button/IconButton"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { Field, type FieldProps } from "@tokenterminal/core/ui/input/Field"
import { type ReactNode } from "react"
import { DropZone, type FileDropItem, FileTrigger } from "react-aria-components"

export type FileDropzoneFieldProps = Omit<FieldProps, "children"> & {
  children?: ReactNode
  /** Description for the field. Should only be used if there's a `label`. */
  description?: string
  /** Optional help text for signposting the user to the file type or size requirements. e.g. "200px x 200px is recommended. Max 5MB" */
  dropzoneSubtext?: string
  file: null | string
  isInvalid?: boolean
  setFile: (file: string | null) => void
}

/**
 * A copy of FileDropzoneField from tt-core, but with a few changes:
 * - Only accepts 1 file at a time
 * - Displays a preview of the image after it's uploaded
 */

export const CustomImageUpload = ({
  description,
  dropzoneSubtext,
  errorMessage,
  file,
  isInvalid,
  label,
  setFile,
}: FileDropzoneFieldProps) => {
  return (
    <Field description={description} errorMessage={errorMessage} label={label}>
      <DropZone
        className="tt-dropzone !h-[100px] !rounded-lg"
        data-invalid={isInvalid ?? undefined}
        onDrop={async (e) => {
          const eventFile = e.items[0] as FileDropItem
          const file = await eventFile.getFile()
          const filename = URL.createObjectURL(file)
          setFile(filename)
        }}
      >
        <div className="flex flex-col justify-center items-center h-full gap-2 p-8 text-center relative">
          {file ? (
            <div className="flex flex-col gap-2 items-center">
              <div
                key={file}
                className="flex flex-col items-center gap-2 relative"
              >
                <div
                  className="w-[50px] h-[50px] rounded overflow-hidden bg-gray-100 flex items-center justify-center"
                  style={{ objectFit: "cover" }}
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={file}
                    alt="Uploaded image"
                    className="max-w-full max-h-full"
                  />
                </div>
              </div>
              <IconButton
                label="Remove image"
                className="absolute top-2 right-2"
                variant="ghost"
                size="small"
                icon={X}
                onPress={() => setFile(null)}
              />
            </div>
          ) : (
            <div className="flex flex-col justify-center items-center gap-1">
              <Icon as={UploadSimple} className="w-4 text-fg-secondary" />
              <div className="flex flex-col">
                <FileTrigger
                  onSelect={(selectedFile) => {
                    if (!selectedFile) {
                      return
                    }

                    const file = Array.from(selectedFile)[0]
                    // @ts-ignore
                    const filename = URL.createObjectURL(file)
                    setFile(filename)
                  }}
                >
                  <Button
                    className="text-fg-secondary"
                    style={{
                      background: "transparent",
                      border: "none",
                      fontWeight: 500,
                      textDecoration: "underline",
                      padding: 0,
                      height: "auto",
                    }}
                  >
                    Browse files
                  </Button>
                </FileTrigger>
                <span slot="label" className="block text-fg-secondary">
                  or drag and drop
                </span>
              </div>
              {dropzoneSubtext && (
                <p className="text-xs text-fg-secondary">{dropzoneSubtext}</p>
              )}
            </div>
          )}
        </div>
      </DropZone>
    </Field>
  )
}
